/**
 * Turn a rendered doc body back into Markdown.
 *
 * The pages are generated from Markdown in the first place, so this is a lossy
 * round trip, not a general-purpose HTML converter: it handles what the backend's
 * renderer emits (headings, paragraphs, lists, code, tables, links, images) and
 * falls back to the element's text for anything else.
 */

const BLOCK = new Set(['P', 'DIV', 'SECTION', 'ARTICLE', 'BLOCKQUOTE', 'PRE', 'UL', 'OL', 'TABLE'])

const clean = (s: string) => s.replace(/\s+/g, ' ').trim()

function inline(node: Node): string {
  if (node.nodeType === Node.TEXT_NODE) return node.textContent ?? ''
  if (node.nodeType !== Node.ELEMENT_NODE) return ''

  const el = node as HTMLElement
  const kids = () => [...el.childNodes].map(inline).join('')

  switch (el.tagName) {
    case 'BR':
      return '\n'
    case 'CODE':
      return `\`${el.textContent ?? ''}\``
    case 'STRONG':
    case 'B':
      return `**${kids()}**`
    case 'EM':
    case 'I':
      return `*${kids()}*`
    case 'A': {
      const href = el.getAttribute('href')
      const text = kids()
      return href ? `[${text}](${href})` : text
    }
    case 'IMG': {
      const src = el.getAttribute('src') ?? ''
      return `![${el.getAttribute('alt') ?? ''}](${src})`
    }
    default:
      return kids()
  }
}

/**
 * What can sit inside an `<li>` and still needs block treatment. Running these through
 * `inline` would flatten them: a fenced code block would lose its fence, its language
 * and every newline, and come back as a one-line code span — which is how a two-command
 * shell snippet in a numbered step turns into one broken command.
 */
const LI_BLOCK = new Set(['P', 'PRE', 'DIV', 'BLOCKQUOTE', 'TABLE', 'UL', 'OL', 'H1', 'H2', 'H3', 'H4', 'HR'])

function listItems(list: HTMLElement): string {
  const ordered = list.tagName === 'OL'

  return [...list.children]
    .filter((li) => li.tagName === 'LI')
    .map((li, i) => {
      const marker = ordered ? `${i + 1}.` : '-'
      // Continuation lines have to clear the marker. Indented any less, Markdown reads
      // them as the end of the list rather than as part of this item.
      const indent = ' '.repeat(marker.length + 1)

      const blocks: HTMLElement[] = []
      const lead: Node[] = []
      for (const node of li.childNodes) {
        const el = node.nodeType === Node.ELEMENT_NODE ? (node as HTMLElement) : null
        if (el && LI_BLOCK.has(el.tagName)) blocks.push(el)
        else if (!blocks.length) lead.push(node)
      }

      let content = clean(lead.map(inline).join(''))

      for (const el of blocks) {
        // A "loose" list wraps the item's own text in a <p>. That is the item's text,
        // not a block sitting below it.
        if (!content && el.tagName === 'P') {
          content = clean(inline(el))
          continue
        }

        const nested = el.tagName === 'UL' || el.tagName === 'OL'
        const text = nested ? listItems(el) : block(el)
        if (!text.trim()) continue

        // A nested list hangs directly off its parent item; every other block is a
        // block of its own and needs the blank line before it.
        content = content ? `${content}${nested ? '\n' : '\n\n'}${text}` : text
      }

      // Hang the content off the marker — first line beside it, the rest indented past
      // it. Indenting the whole tail uniformly is what keeps a fence, a paragraph and a
      // nested list all inside the same item; nesting comes from the parent's indent,
      // so a nested list is rendered flat and pushed over by whoever contains it.
      const [first = '', ...rest] = content.split('\n')
      return [`${marker} ${first}`.trimEnd(), ...rest.map((l) => (l ? indent + l : l))].join('\n')
    })
    .join('\n')
}

/** An unescaped pipe opens a new cell, so a cell reading `draft|sent` would widen its
 *  row past the header and the parser would drop the overflow. GFM wants `\|` here —
 *  inside code spans too, where the backticks do not protect it. */
const cellText = (cell: Element) => clean(inline(cell)).replace(/\|/g, '\\|')

function table(el: HTMLElement): string {
  const rows = [...el.querySelectorAll('tr')].map((tr) => [...tr.children].map(cellText))
  if (!rows.length) return ''

  const [head, ...body] = rows
  const width = head!.length
  const lines = [`| ${head!.join(' | ')} |`, `| ${Array(width).fill('---').join(' | ')} |`]
  for (const r of body) lines.push(`| ${r.join(' | ')} |`)
  return lines.join('\n')
}

function block(el: HTMLElement): string {
  switch (el.tagName) {
    case 'H1':
      return `# ${clean(inline(el))}`
    case 'H2':
      return `## ${clean(inline(el))}`
    case 'H3':
      return `### ${clean(inline(el))}`
    case 'H4':
      return `#### ${clean(inline(el))}`

    case 'PRE': {
      // Chroma wraps every token in a span; textContent is the only faithful source.
      const code = el.textContent ?? ''
      const lang = el.querySelector('code')?.className.match(/language-(\w+)/)?.[1] ?? ''
      return `\`\`\`${lang}\n${code.replace(/\n$/, '')}\n\`\`\``
    }

    case 'UL':
    case 'OL':
      return listItems(el)

    case 'TABLE':
      return table(el)

    case 'BLOCKQUOTE':
      return (el.textContent ?? '')
        .trim()
        .split('\n')
        .map((l) => `> ${l.trim()}`)
        .join('\n')

    case 'HR':
      return '---'

    case 'DIV':
      // A wrapper — the table scroll container, Chroma's highlight box — contributes
      // nothing of its own; its children carry the content. Handling it here and not
      // only at the top level is what lets a highlighted code block survive inside a
      // list item, where the wrapper sits between the <li> and the <pre>.
      return el.children.length ? htmlToMarkdown(el.innerHTML) : clean(inline(el))

    default:
      // A <br/> yields "\n", and the source newline after it yields another. Left
      // alone the pair reads as a paragraph break in Markdown, splitting one
      // paragraph in two. Collapse runs back to the single break that was meant.
      return inline(el)
        .replace(/[ \t]*\n[ \t]*/g, '\n')
        .replace(/\n{2,}/g, '\n')
        .trim()
  }
}

export function htmlToMarkdown(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html')
  const out: string[] = []

  for (const child of doc.body.children) {
    const el = child as HTMLElement
    const text = block(el)
    if (text.trim()) out.push(text)
    else if (!BLOCK.has(el.tagName) && el.textContent?.trim()) out.push(clean(el.textContent))
  }

  return out.join('\n\n')
}
